import React from 'react';
import { 
  Table,
  Image,
  Tag,
  Space,
  Button,
  message
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Product } from '../types/product';
import {
  getCategoryLabel,
  getChildCategoryLabel,
  pickChildCategoryFields,
} from '../constants/productCategory';

interface ProductTableProps {
  products: Product[];
  loading?: boolean;
  total?: number;
  current?: number;
  pageSize?: number;
  onPageChange?: (page: number, size: number) => void;
  showActions?: boolean;
  onRemoveFromGroup?: (productId: string) => void;
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  loading = false,
  total = 0,
  current = 1,
  pageSize = 20,
  onPageChange,
  showActions = true,
  onRemoveFromGroup
}) => {
  const navigate = useNavigate();

  // 处理查看商品详情
  const handleView = (record: Product) => {
    navigate(`/products/detail/${record.id}`);
  };

  // 处理编辑商品
  const handleEdit = (record: Product) => {
    navigate(`/products/edit/${record.id}`);
  };

  // 处理从商品组中移除商品
  const handleRemoveFromGroup = (record: Product) => {
    if (onRemoveFromGroup) {
      onRemoveFromGroup(record.id);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '商品图片',
      dataIndex: 'logoUrl',
      key: 'logoUrl',
      width: 80,
      render: (logoUrl: string) => (
        <Image
          width={40}
          height={40}
          src={logoUrl}
          alt="商品图片"
          style={{ borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
        />
      ),
    },
    {
      title: '商品名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: '商品类型',
      dataIndex: 'category',
      key: 'category',
      render: (_: string, record: Product) => (
        <Tag color="blue">{getCategoryLabel(record.category, record.categoryDesc)}</Tag>
      ),
    },
    {
      title: '商品子类',
      dataIndex: 'childCategory',
      key: 'childCategory',
      render: (_: string, record: Product) => {
        const { code, desc } = pickChildCategoryFields(record);
        return <Tag color="green">{getChildCategoryLabel(code, desc)}</Tag>;
      },
    },
    {
      title: '原价',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `¥${price?.toFixed(2) || '0.00'}`,
    },
    {
      title: '折扣价',
      dataIndex: 'discountPrice',
      key: 'discountPrice',
      render: (discountPrice: number) => `¥${discountPrice?.toFixed(2) || '0.00'}`,
    },
    {
      title: '状态',
      dataIndex: 'isShow',
      key: 'isShow',
      render: (isShow: number) => (
        <Tag color={isShow === 1 ? 'green' : 'red'}>
          {isShow === 1 ? '已上架' : '已下架'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'seq',
      key: 'seq',
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (date: string) => date ? new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-') : '--',
    },
    {
      title: '修改时间',
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      render: (date: string) => date ? new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-') : '--',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: Product) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            查看
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          {onRemoveFromGroup && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveFromGroup(record)}
              size="small"
            >
              移除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={products}
      loading={loading}
      rowKey="id"
      pagination={{
        current,
        pageSize,
        total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
        onChange: onPageChange,
        onShowSizeChange: onPageChange,
      }}
      scroll={{ x: 1600 }}
    />
  );
};

export default ProductTable;
